import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adminAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import {
  Sparkles, Pause, Play, Upload, RefreshCw, Trash2, Image, CheckSquare, Square, AlertTriangle, XCircle, FileArchive,
} from 'lucide-react';

// Admin: pictures for picture questions. Questions without a picture stay hidden from players.
// "Generate" puts them in a queue; the server draws them with AI in the background (no time limit,
// the page can be closed). Real pictures can be uploaded one by one or as a .zip.

const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${process.env.REACT_APP_BACKEND_URL || ''}${url}`;
};

const STATUS_INFO = {
  missing: { label: 'Missing - AI can draw', color: 'bg-amber-100 text-amber-800' },
  queued: { label: 'In queue', color: 'bg-sky-100 text-sky-800' },
  generating: { label: 'Generating now', color: 'bg-indigo-100 text-indigo-800' },
  ai: { label: 'Done by AI', color: 'bg-teal-100 text-teal-800' },
  uploaded: { label: 'Real picture uploaded', color: 'bg-emerald-100 text-emerald-800' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800' },
  photo_needed: { label: 'Real picture needed', color: 'bg-slate-200 text-slate-800' },
};
const ORDER = ['missing', 'queued', 'generating', 'ai', 'uploaded', 'failed', 'photo_needed'];
const PAGE = 48;

const errText = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;

function formatEta(minutes) {
  if (minutes == null) return null;
  if (minutes < 60) return `about ${Math.max(1, minutes)} min`;
  const h = Math.floor(minutes / 60);
  return `about ${h} h ${minutes % 60} min`;
}

export function PictureImagesPanel() {
  const [status, setStatus] = useState(null);
  const [filter, setFilter] = useState('missing');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [list, setList] = useState({ items: [], total: 0 });
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const zipRef = useRef(null);
  const fileRef = useRef(null);
  const uploadFor = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await adminAPI.getPictureStatus();
      setStatus(res.data);
    } catch (e) {
      /* keep the last status */
    }
  }, []);

  const loadList = useCallback(async () => {
    try {
      const res = await adminAPI.getPictureQuestions({ status: filter, search: search.trim() || undefined, page, limit: PAGE });
      setList(res.data);
    } catch (e) {
      toast.error(errText(e, 'Could not load picture questions'));
    }
  }, [filter, search, page]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadList(); }, [loadList]);

  const state = status?.job?.state;
  const active = state === 'running' || (status?.counts?.queued || 0) + (status?.counts?.generating || 0) > 0;
  useEffect(() => {
    if (!active) return undefined;
    const a = setInterval(loadStatus, 5000);
    const b = setInterval(loadList, 20000);
    return () => { clearInterval(a); clearInterval(b); };
  }, [active, loadStatus, loadList]);

  const refresh = async () => { await Promise.all([loadStatus(), loadList()]); };

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const res = await fn();
      if (ok) toast.success(typeof ok === 'function' ? ok(res.data) : ok);
      setSelected(new Set());
      await refresh();
    } catch (e) {
      toast.error(errText(e, 'Something went wrong'));
    } finally {
      setBusy(false);
    }
  };

  const counts = status?.counts || {};
  const missing = counts.missing || 0;
  const cost = status ? (missing * (status.estimated_cost_per_image_usd || 0)).toFixed(0) : '0';
  const queuedNow = (counts.queued || 0) + (counts.generating || 0);
  const job = status?.job || {};
  const doneInRun = (job.done || 0) + (job.failed || 0);
  const runTotal = doneInRun + queuedNow;
  const pct = runTotal ? Math.round((doneInRun / runTotal) * 100) : 0;

  const generateAll = () => run(() => adminAPI.generatePictures({ all_missing: true }),
    (d) => `${d.queued} pictures queued. AI is generating them in the background - you can close this page.`);
  const retryFailed = () => run(() => adminAPI.generatePictures({ all_missing: true, include_failed: true }),
    (d) => `${d.queued} pictures queued again.`);
  const generateSelected = () => run(() => adminAPI.generatePictures({ ids: Array.from(selected) }),
    (d) => (d.queued ? `${d.queued} pictures queued.` : 'Nothing queued (AI does not draw people, brands, films or artworks).'));

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const allOnPage = list.items.length > 0 && list.items.every((i) => selected.has(i._id));
  const togglePage = () => setSelected((s) => {
    const n = new Set(s);
    list.items.forEach((i) => (allOnPage ? n.delete(i._id) : n.add(i._id)));
    return n;
  });

  const onZip = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const id = toast.loading('Uploading pictures...');
    await run(() => adminAPI.uploadPictureZip(file), (d) => {
      toast.dismiss(id);
      return `${d.attached} pictures attached${d.unmatched_count ? `, ${d.unmatched_count} files did not match any question` : ''}.`;
    });
    toast.dismiss(id);
  };

  const onOneFile = async (e) => {
    const file = e.target.files?.[0];
    const qid = uploadFor.current;
    e.target.value = '';
    if (!file || !qid) return;
    await run(() => adminAPI.uploadQuestionPicture(qid, file), 'Picture saved - the question is now active.');
  };

  const pages = Math.max(1, Math.ceil((list.total || 0) / PAGE));

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5 space-y-5" data-testid="picture-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Image className="w-5 h-5 text-teal-600" /> Pictures for picture questions
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Questions without a picture are hidden from players. AI draws animals, food, places and objects in the
            background; people, brands, films and artworks need a real picture.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={busy} data-testid="picture-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {status && !status.api_key_configured && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="picture-no-key">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>No OpenAI API key is saved. Add it in Admin &gt; Settings, then click Generate.</span>
        </div>
      )}

      {/* status cards = filters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {ORDER.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => { setFilter(s); setPage(1); setSelected(new Set()); }}
            className={`text-left rounded-lg border p-3 transition ${filter === s ? 'border-teal-500 ring-2 ring-teal-200' : 'border-slate-200 hover:border-slate-300'}`}
            data-testid={`picture-filter-${s}`}
          >
            <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${STATUS_INFO[s].color}`}>{STATUS_INFO[s].label}</span>
            <span className="block text-2xl font-bold text-slate-900 mt-1">{counts[s] ?? '-'}</span>
          </button>
        ))}
      </div>

      {/* job */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="picture-job">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium text-slate-900">AI generation:</span>
          <span data-testid="picture-job-state" className={`rounded px-2 py-0.5 text-xs font-semibold ${
            state === 'running' ? 'bg-indigo-100 text-indigo-800' : state === 'paused' ? 'bg-amber-100 text-amber-800'
              : state === 'finished' ? 'bg-teal-100 text-teal-800' : 'bg-slate-200 text-slate-700'}`}
          >
            {state === 'running' ? (job.running ? 'Running' : 'Starting...') : state === 'paused' ? 'Paused' : state === 'finished' ? 'Finished' : 'Not running'}
          </span>
          {runTotal > 0 && <span className="text-slate-600">{doneInRun} of {runTotal} done{job.failed ? ` (${job.failed} failed)` : ''}</span>}
          {state === 'running' && formatEta(job.eta_minutes) && <span className="text-slate-600">· {formatEta(job.eta_minutes)} left</span>}
          {job.model && <span className="text-slate-400">· {job.model}</span>}
        </div>
        {runTotal > 0 && (
          <div className="h-2 rounded bg-slate-200 overflow-hidden">
            <div className="h-2 bg-teal-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        )}
        {job.last_error && state !== 'finished' && (
          <div className="flex items-start gap-2 text-sm text-red-700" data-testid="picture-job-error">
            <XCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span className="break-words min-w-0">{job.last_error}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button onClick={generateAll} disabled={busy || !missing} className="bg-gradient-to-r from-teal-500 to-teal-600" data-testid="picture-generate-all">
            <Sparkles className="w-4 h-4 mr-2" /> Generate all missing ({missing})
          </Button>
          {selected.size > 0 && (
            <Button onClick={generateSelected} disabled={busy} variant="outline" data-testid="picture-generate-selected">
              <Sparkles className="w-4 h-4 mr-2" /> Generate selected ({selected.size})
            </Button>
          )}
          {(counts.failed || 0) > 0 && (
            <Button onClick={retryFailed} disabled={busy} variant="outline" data-testid="picture-retry-failed">
              <RefreshCw className="w-4 h-4 mr-2" /> Retry failed ({counts.failed})
            </Button>
          )}
          {state === 'running' && (
            <Button onClick={() => run(() => adminAPI.pausePictures(), 'Paused after the pictures being drawn now.')} disabled={busy} variant="outline" data-testid="picture-pause">
              <Pause className="w-4 h-4 mr-2" /> Pause
            </Button>
          )}
          {state === 'paused' && queuedNow > 0 && (
            <Button onClick={() => run(() => adminAPI.resumePictures(), 'Generation continues.')} disabled={busy} variant="outline" data-testid="picture-resume">
              <Play className="w-4 h-4 mr-2" /> Resume ({queuedNow})
            </Button>
          )}
          {state !== 'running' && (counts.queued || 0) > 0 && (
            <Button onClick={() => run(() => adminAPI.cancelPictureQueue(), (d) => `${d.removed_from_queue} removed from the queue.`)} disabled={busy} variant="outline">
              Empty queue
            </Button>
          )}
          <input ref={zipRef} type="file" accept=".zip" className="hidden" onChange={onZip} data-testid="picture-zip-input" />
          <Button onClick={() => zipRef.current?.click()} disabled={busy} variant="outline" data-testid="picture-zip">
            <FileArchive className="w-4 h-4 mr-2" /> Upload real pictures (.zip)
          </Button>
        </div>
        {missing > 0 && (
          <p className="text-xs text-slate-500">
            Estimated cost for {missing} pictures: about ${cost} on your OpenAI account. The server keeps working when you close this page;
            it can take several hours.
          </p>
        )}
        <p className="text-xs text-slate-500">
          Zip: name the files like in the Excel picture link (e.g. 1-pic/cities/berlin.jpg or berlin.jpg). A real picture replaces the AI one.
        </p>
      </div>

      {/* list */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search topic or answer..."
          className="max-w-xs"
          data-testid="picture-search"
        />
        <button type="button" onClick={togglePage} className="flex items-center gap-2 text-sm text-slate-700 hover:text-teal-600" data-testid="picture-select-page">
          {allOnPage ? <CheckSquare className="w-5 h-5 text-teal-600" /> : <Square className="w-5 h-5" />}
          Select this page
        </button>
        <span className="text-sm text-slate-500">{list.total} questions · {STATUS_INFO[filter]?.label}</span>
      </div>

      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onOneFile} />
      {list.items.length === 0 ? (
        <p className="text-center text-slate-500 py-8">No questions here.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          {list.items.map((q) => (
            <div key={q._id} className={`rounded-lg border overflow-hidden ${selected.has(q._id) ? 'border-teal-500 ring-2 ring-teal-200' : 'border-slate-200'}`} data-testid={`picture-item-${q._id}`}>
              <div className="relative aspect-square bg-slate-100">
                <button type="button" onClick={() => toggle(q._id)} aria-label="Select"
                  className={`absolute top-2 left-2 z-10 w-6 h-6 rounded flex items-center justify-center ${selected.has(q._id) ? 'bg-teal-500 text-white' : 'bg-white/90 text-slate-500'}`}>
                  {selected.has(q._id) ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                </button>
                {q.image_url ? (
                  <img src={resolveImageUrl(q.image_url)} alt={q.answer} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 text-xs p-2 text-center">
                    <Image className="w-8 h-8 mb-1" />
                    {q.image_status === 'generating' ? 'AI is drawing...' : 'No picture yet'}
                  </div>
                )}
                <span className={`absolute bottom-2 left-2 rounded px-1.5 py-0.5 text-[10px] font-medium ${STATUS_INFO[q.image_status]?.color || ''}`}>
                  {STATUS_INFO[q.image_status]?.label || q.image_status}
                </span>
              </div>
              <div className="p-2 space-y-1">
                <p className="text-xs text-teal-500 font-medium truncate" title={q.topic}>{q.topic}</p>
                <p className="text-sm font-semibold text-slate-900 truncate" title={q.answer}>{q.answer}</p>
                {q.image_error && <p className="text-[11px] text-red-600 line-clamp-2" title={q.image_error}>{q.image_error}</p>}
                <div className="flex gap-1 pt-1">
                  <Button size="sm" variant="outline" className="h-7 px-2" title="Upload a real picture"
                    onClick={() => { uploadFor.current = q._id; fileRef.current?.click(); }}>
                    <Upload className="w-3.5 h-3.5" />
                  </Button>
                  {q.image_policy === 'ai' && !['queued', 'generating'].includes(q.image_status) && (
                    <Button size="sm" variant="outline" className="h-7 px-2" title={q.image_url ? 'Draw again with AI' : 'Draw with AI'}
                      onClick={() => run(() => adminAPI.generatePictures({ ids: [q._id] }), 'Queued.')}>
                      <Sparkles className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {q.image_url && (
                    <Button size="sm" variant="outline" className="h-7 px-2 text-red-600" title="Remove this picture (question is hidden again)"
                      onClick={() => run(() => adminAPI.rejectPicture(q._id), 'Picture removed.')}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 text-sm">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span className="text-slate-600">Page {page} / {pages}</span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}
    </section>
  );
}
